import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageSquare } from 'lucide-react';

export function ContactCTA() {
  const { currentLanguage } = useLanguage();

  const content = {
    en: {
      label: 'Contact',
      title: "Let's Build Something Great",
      subtitle: 'Have a project in mind? We\'d love to hear about it and explore how we can help.',
      cta: 'Get in Touch',
      note: 'Free consultation · NDA ready · 48h response'
    },
    tr: {
      label: 'İletişim',
      title: 'Harika Bir Şey İnşa Edelim',
      subtitle: 'Aklınızda bir proje mi var? Duymak ve nasıl yardımcı olabileceğimizi keşfetmek isteriz.',
      cta: 'İletişime Geçin',
      note: 'Ücretsiz danışmanlık · NDA hazır · 48 saat yanıt'
    },
    de: {
      label: 'Kontakt',
      title: 'Lass Uns Etwas Großartiges Bauen',
      subtitle: 'Haben Sie ein Projekt im Sinn? Wir würden gerne davon hören und erkunden, wie wir helfen können.',
      cta: 'Kontakt aufnehmen',
      note: 'Kostenlose Beratung · NDA bereit · 48h Antwort'
    }
  };

  const t = content[currentLanguage] || content.en;

  return (
    <section
      id="contact"
      data-testid="contact-cta-section"
      className="relative py-20 md:py-28"
      style={{ background: 'var(--bg-elevated)' }}
    >
      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8">
        {/* Two-column layout */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-10">
          {/* Left: Text content */}
          <div className="flex-1">
            <p 
              className="text-xs uppercase tracking-widest mb-4"
              style={{ color: '#1C7C7D' }}
            >
              {t.label}
            </p>
            <h2 
              className="text-3xl md:text-4xl font-semibold tracking-tight mb-4"
              style={{ color: 'var(--text-primary)' }}
              data-testid="contact-cta-title"
            >
              {t.title}
            </h2>
            <p 
              className="text-base md:text-lg mb-6"
              style={{ color: 'var(--text-muted)' }}
            >
              {t.subtitle}
            </p>
            <p 
              className="text-xs"
              style={{ color: 'var(--text-subtle)', fontFamily: 'JetBrains Mono, monospace' }}
            >
              {t.note}
            </p>
          </div>

          {/* Right: CTA Card */}
          <div 
            className="flex-shrink-0"
          >
            <Link
              to="/contact"
              data-testid="contact-cta-button"
              className="group flex items-center gap-6 px-8 py-6 rounded-2xl transition-all duration-300 hover:scale-[1.02]"
              style={{ 
                background: 'linear-gradient(135deg, rgba(15, 61, 62, 0.35) 0%, rgba(15, 61, 62, 0.15) 100%)',
                border: '1px solid rgba(28, 124, 125, 0.25)'
              }}
            >
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ 
                  background: 'linear-gradient(135deg, #0F3D3E 0%, #1C7C7D 100%)'
                }}
              >
                <MessageSquare className="w-6 h-6" style={{ color: '#E6EDF3' }} />
              </div>
              <div>
                <span 
                  className="text-lg font-semibold block group-hover:text-[#1C7C7D] transition-colors"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {t.cta}
                </span>
                <span 
                  className="text-sm flex items-center gap-1 mt-1 group-hover:gap-2 transition-all"
                  style={{ color: '#1C7C7D' }}
                >
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom separator - finality break */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ background: 'rgba(28, 124, 125, 0.08)' }}
      />
    </section>
  );
}
